import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { ConversationService, AgentService } from '../services/api';
import { API_CONFIG } from '../config/apiConfig';

export interface Agent {
  id: string;
  name: string;
  type: string;
  custom_name?: string;
  avatar_url?: string;
  color?: string;
  avatar_fallback_color?: string;
  is_active: boolean;
  status?: 'idle' | 'thinking' | 'typing' | 'error';
}

export interface Message {
  id: string;
  conversation_id: string;
  user_agent_id?: string;
  parent_message_id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  content_type?: 'text' | 'html' | 'markdown';
  rendering_hints?: {
    allowScripts?: boolean;
    allowIframes?: boolean;
    sanitizationLevel?: 'strict' | 'moderate' | 'minimal';
  };
  metadata: Record<string, any>;
  tokens_used?: number;
  created_at: Date;
  edited_at?: Date;
  edit_history?: Array<{ content: string; edited_at: Date }>;
  reactions?: MessageReaction[];
  thread_messages?: Message[];
  agent?: Agent;
  // Agent info for display (populated by backend)
  agent_name?: string;
  agent_avatar?: string;
  agent_type?: string;
  custom_name?: string;
  avatar_url?: string;
  color?: string;
  avatar_fallback_color?: string;
  // Messaging extensions
  sender_name?: string;
  sender_id?: string;
}

export interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: Date;
}

export interface Conversation {
  id: string;
  user_id: string;
  title?: string;
  context: Record<string, any>;
  metadata?: Record<string, any>;
  is_active: boolean;
  graph_node_id?: string;
  created_at: Date;
  updated_at: Date;
  last_message_at?: Date;
  message_count?: number;
  // Messaging extensions
  conversation_type?: 'ai_chat' | 'dm' | 'group' | 'channel';
  slug?: string;
  description?: string;
  is_public?: boolean;
  creator_id?: string;
  history_visible_to_new_members?: boolean;
  participant_count?: number;
  unread_count?: number;
  has_mention?: boolean;
}

export interface UnreadCount {
  conversationId: string;
  count: number;
  hasMention: boolean;
}

interface ChatState {
  // State
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  agents: Agent[];
  socket: Socket | null;
  isConnected: boolean;
  typingUsers: Set<string>;
  typingAgents: Set<string>;
  processingAgents: Set<string>;
  onlineUsers: string[];
  showHtmlRenderer: boolean;

  // Task suggestions state
  taskSuggestions: Array<{
    title: string;
    description?: string;
    suggestedAssignee?: string;
    suggestedPriority?: 'low' | 'medium' | 'high' | 'urgent';
  }>;
  availableTeammates: Array<{
    id: string;
    name: string;
    type: string;
    isCustom: boolean;
  }>;
  taskSuggestionsMetadata?: {
    sourceAgent: string;
    detectedAt: string;
    conversationId?: string;
  };
  showTaskSuggestions: boolean;
  isDetectingTasks: boolean;

  // Messaging state
  conversationsLoaded: boolean;
  unreadCounts: Map<string, UnreadCount>;
  channels: Conversation[];
  humanTypingUsers: Map<string, { userId: string; isTyping: boolean }>;

  // Actions
  initializeSocket: (token: string) => void;
  disconnectSocket: () => void;
  loadConversations: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  createConversation: (title?: string, context?: any) => Promise<Conversation>;
  loadAgents: () => Promise<void>;
  sendMessage: (content: string, agentId?: string, parentMessageId?: string) => Promise<void>;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  addReaction: (messageId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string) => Promise<void>;
  setCurrentConversation: (conversation: Conversation | null) => void;
  toggleHtmlRenderer: () => void;
  updateAgentStatus: (agentId: string, status: Agent['status']) => void;
  clearTaskSuggestions: () => void;
  detectTasksFromMessage: (messageId: string) => Promise<void>;
  // Messaging actions
  loadChannels: () => Promise<void>;
  markConversationRead: (conversationId: string) => void;
  updateUnreadCount: (conversationId: string, count: number, hasMention: boolean) => void;
  emitTyping: (isTyping: boolean) => void;
}

const conversationService = new ConversationService();
const agentService = new AgentService();

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  conversations: [],
  currentConversation: null,
  messages: [],
  agents: [],
  socket: null,
  isConnected: false,
  typingUsers: new Set(),
  typingAgents: new Set(),
  processingAgents: new Set(),
  onlineUsers: [],
  showHtmlRenderer: true, // Default to true - this means HTML will be rendered (not shown as raw)

  // Messaging initial state
  conversationsLoaded: false,
  unreadCounts: new Map(),
  channels: [],
  humanTypingUsers: new Map(),

  // Task suggestions initial state
  taskSuggestions: [],
  availableTeammates: [],
  taskSuggestionsMetadata: undefined,
  showTaskSuggestions: false,
  isDetectingTasks: false,

  // Initialize WebSocket connection
  initializeSocket: (token: string) => {
    const existingSocket = get().socket;
    if (existingSocket) {
      console.log('[ChatStore] WebSocket already initialized, skipping');
      return;
    }

    // Use API configuration for WebSocket URL
    const wsUrl = API_CONFIG.WS_URL;
    console.log('[ChatStore] API_CONFIG.WS_URL:', wsUrl);
    console.log('[ChatStore] API_CONFIG.BASE_URL:', API_CONFIG.BASE_URL);
    console.log('[ChatStore] Environment VITE_CONVERSATION_WS_URL:', import.meta.env.VITE_CONVERSATION_WS_URL);
    console.log('[ChatStore] Environment VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);

    // Ensure we're using the correct WebSocket URL
    let finalWsUrl = wsUrl;
    if (!wsUrl || wsUrl.includes('prismaticforge.ai/') || wsUrl === 'wss://prismaticforge.ai') {
      // Force the correct API subdomain if we detect the wrong URL
      finalWsUrl = 'wss://api.prismaticforge.ai';
      console.log('[ChatStore] Correcting WebSocket URL to:', finalWsUrl);
    }

    console.log('[ChatStore] Final WebSocket URL:', finalWsUrl);
    const socket = io(finalWsUrl, {
      path: '/socket.io/',
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling'], // Prefer WebSocket but allow polling fallback
    });

    socket.on('connect', () => {
      console.log('[ChatStore] WebSocket connected successfully');
      set({ isConnected: true });
      
      // Join current conversation if any
      const { currentConversation } = get();
      if (currentConversation) {
        console.log('[ChatStore] Joining conversation:', currentConversation.id);
        socket.emit('join:conversation', currentConversation.id);
      }
    });

    socket.on('disconnect', () => {
      console.log('[ChatStore] WebSocket disconnected');
      set({ isConnected: false });
    });

    socket.on('connect_error', (error) => {
      console.error('[ChatStore] WebSocket connection error:', error.message);
    });

    // Message events
    socket.on('message:new', (data: { conversationId: string; message: Message }) => {
      console.log('[ChatStore] Received new message:', {
        conversationId: data.conversationId,
        messageId: data.message.id,
        role: data.message.role,
        user_agent_id: data.message.user_agent_id,
        agent_name: data.message.agent_name,
        custom_name: data.message.custom_name,
        fullMessage: data.message
      });
      const { currentConversation, messages, processingAgents, typingAgents } = get();
      if (currentConversation?.id === data.conversationId) {
        // Check if message already exists (deduplication)
        const messageExists = messages.some(m => m.id === data.message.id);
        if (messageExists) {
          console.log('[ChatStore] Message already exists, skipping duplicate:', data.message.id);
          return;
        }
        console.log('[ChatStore] Adding message to current conversation');
        set({ messages: [...messages, data.message] });
        
        // Clear processing and typing states when message arrives
        if (data.message.role === 'assistant' && data.message.user_agent_id) {
          const newProcessingAgents = new Set(processingAgents);
          const newTypingAgents = new Set(typingAgents);
          
          // Clear the specific agent from processing and typing
          newProcessingAgents.delete(data.message.user_agent_id);
          newProcessingAgents.delete('system');
          newTypingAgents.delete(data.message.user_agent_id);
          
          set({ 
            processingAgents: newProcessingAgents,
            typingAgents: newTypingAgents
          });
        }
      } else {
        console.log('[ChatStore] Message is for different conversation, ignoring');
      }
    });

    socket.on('message:edited', (data: { messageId: string; conversationId: string; editedAt: string }) => {
      const { messages } = get();
      set({
        messages: messages.map((msg) =>
          msg.id === data.messageId ? { ...msg, edited_at: new Date(data.editedAt) } : msg
        ),
      });
    });

    // Reaction events
    socket.on('reaction:added', (data: { messageId: string; userId: string; emoji: string }) => {
      const { messages } = get();
      set({
        messages: messages.map((msg) => {
          if (msg.id === data.messageId) {
            const reactions = msg.reactions || [];
            return {
              ...msg,
              reactions: [...reactions, {
                id: `temp-${Date.now()}`,
                message_id: data.messageId,
                user_id: data.userId,
                emoji: data.emoji,
                created_at: new Date(),
              }],
            };
          }
          return msg;
        }),
      });
    });

    // Typing indicators
    socket.on('user:typing', (data: { conversationId: string; userId: string; status: string }) => {
      const { typingUsers } = get();
      const newTypingUsers = new Set(typingUsers);
      if (data.status === 'typing') {
        newTypingUsers.add(data.userId);
      } else {
        newTypingUsers.delete(data.userId);
      }
      set({ typingUsers: newTypingUsers });
    });

    socket.on('agent:typing', (data: { conversationId: string; agentId: string; status: string }) => {
      const { typingAgents, processingAgents } = get();
      const newTypingAgents = new Set(typingAgents);
      const newProcessingAgents = new Set(processingAgents);
      
      if (data.status === 'typing') {
        newTypingAgents.add(data.agentId);
        // When agent starts typing, remove from processing
        newProcessingAgents.delete(data.agentId);
        newProcessingAgents.delete('system');
      } else {
        newTypingAgents.delete(data.agentId);
      }
      
      set({ 
        typingAgents: newTypingAgents,
        processingAgents: newProcessingAgents
      });
    });

    // Agent status
    socket.on('agent:status', (data: { agentId: string; status: string }) => {
      get().updateAgentStatus(data.agentId, data.status as Agent['status']);
    });

    // Task detection started (automatic detection)
    socket.on('task_detection:started', (data: { conversationId: string }) => {
      const state = get();
      if (state.currentConversation?.id === data.conversationId) {
        set({ isDetectingTasks: true });
      }
    });

    // Task detection completed with no tasks
    socket.on('task_detection:completed', (data: { conversationId: string; tasksFound: boolean }) => {
      const state = get();
      if (state.currentConversation?.id === data.conversationId && !data.tasksFound) {
        set({ isDetectingTasks: false });
      }
    });

    // Task suggestions from agent responses
    socket.on('task_suggestions', (data: {
      conversationId: string;
      tasks: Array<{
        title: string;
        description?: string;
        suggestedAssignee?: string;
        suggestedPriority?: 'low' | 'medium' | 'high' | 'urgent';
      }>;
      availableTeammates: Array<{
        id: string;
        name: string;
        type: string;
        isCustom: boolean;
      }>;
      metadata: {
        sourceAgent: string;
        detectedAt: string;
      };
    }) => {
      const state = get();
      if (state.currentConversation?.id === data.conversationId) {
        // Store the task suggestions for the UI to handle
        set({
          taskSuggestions: data.tasks,
          availableTeammates: data.availableTeammates,
          taskSuggestionsMetadata: data.metadata,
          showTaskSuggestions: true,
          isDetectingTasks: false // Clear loading state
        });
      }
    });

    // Unread count updates
    socket.on('unread:update', (data: { conversationId: string; unreadCount?: number; hasMention?: boolean }) => {
      const { unreadCounts, currentConversation } = get();
      // Don't increment if we're currently viewing this conversation
      if (currentConversation?.id === data.conversationId) return;
      const newCounts = new Map(unreadCounts);
      const existing = newCounts.get(data.conversationId);
      newCounts.set(data.conversationId, {
        conversationId: data.conversationId,
        count: (existing?.count || 0) + 1,
        hasMention: data.hasMention || existing?.hasMention || false,
      });
      set({ unreadCounts: newCounts });
    });

    // Human typing indicators (from other users in multi-participant chats)
    socket.on('user:typing', (data: { userId: string; conversationId: string; isTyping: boolean }) => {
      const { humanTypingUsers, currentConversation } = get();
      if (currentConversation?.id !== data.conversationId) return;
      const newMap = new Map(humanTypingUsers);
      if (data.isTyping) {
        newMap.set(data.userId, { userId: data.userId, isTyping: true });
      } else {
        newMap.delete(data.userId);
      }
      set({ humanTypingUsers: newMap });
    });

    // Participant events
    socket.on('participant:added', (data: { conversationId: string; addedUserIds?: string[] }) => {
      // Reload conversations to pick up the new participant
      get().loadConversations();
    });

    socket.on('participant:removed', (data: { conversationId: string; removedUserId: string }) => {
      get().loadConversations();
    });

    // Presence updates
    socket.on('presence:update', (data: { userId: string; status: string }) => {
      const { onlineUsers } = get();
      if (data.status === 'online') {
        if (!onlineUsers.includes(data.userId)) {
          set({ onlineUsers: [...onlineUsers, data.userId] });
        }
      } else {
        set({ onlineUsers: onlineUsers.filter(id => id !== data.userId) });
      }
    });

    set({ socket });
  },

  disconnectSocket: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false });
    }
  },

  loadConversations: async () => {
    try {
      const conversations = await conversationService.getConversations();
      set({ conversations, conversationsLoaded: true });
    } catch (error: any) {
      console.error('Failed to load conversations:', error);
      // Don't set empty conversations on 401 errors - let the interceptor handle it
      if (error?.response?.status !== 401) {
        set({ conversations: [] });
      }
      // Don't set conversationsLoaded on failure to prevent auto-creation
    }
  },

  loadConversation: async (conversationId: string) => {
    const state = get();

    // Prevent duplicate loading of the same conversation
    if (state.currentConversation?.id === conversationId && state.messages.length > 0) {
      console.log('[ChatStore] Conversation already loaded, skipping API call');
      return;
    }

    try {
      console.log('[ChatStore] Loading conversation:', conversationId);
      const { conversation, messages } = await conversationService.getConversationWithMessages(conversationId);
      set({ currentConversation: conversation, messages });

      // Join conversation room
      const { socket } = get();
      if (socket) {
        socket.emit('join:conversation', conversationId);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
      // Don't clear messages on error to prevent UI flickering
    }
  },

  createConversation: async (title?: string, context?: any) => {
    try {
      const conversation = await conversationService.createConversation({ title, context });
      const state = get();
      const currentConversations = state.conversations || [];
      set({ conversations: [conversation, ...currentConversations], currentConversation: conversation, messages: [] });
      
      // Join new conversation room
      const { socket } = get();
      if (socket) {
        socket.emit('join:conversation', conversation.id);
      }
      
      return conversation;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      throw error;
    }
  },

  loadAgents: async () => {
    try {
      console.log('[ChatStore] Loading agents...');
      // Try org-scoped agents first, fall back to user agents
      let agents;
      try {
        const { messagingService } = await import('../services/api/messaging.service');
        agents = await messagingService.getOrgAgents();
        console.log('[ChatStore] Loaded org-scoped agents:', agents.length);
      } catch {
        // Fall back to user-scoped agents
        agents = await agentService.getAgents();
        console.log('[ChatStore] Loaded user-scoped agents:', agents.length);
      }
      set({ agents });
    } catch (error: any) {
      console.error('[ChatStore] Failed to load agents:', error);
      if (error?.response?.status !== 401) {
        set({ agents: [] });
      }
    }
  },

  sendMessage: async (content: string, agentId?: string, parentMessageId?: string) => {
    const { currentConversation, socket, processingAgents } = get();
    if (!currentConversation) return;

    // Prevent duplicate sends
    const state = get() as any;
    if (state.isSendingMessage) {
      console.log('[ChatStore] Already sending a message, ignoring duplicate request');
      return;
    }
    
    set({ isSendingMessage: true } as any);
    
    // Set processing state immediately when sending a message
    // Extract @mentions to determine which agent will respond
    let targetAgentId = agentId;
    if (!targetAgentId && content.includes('@')) {
      // Extract agent name from @mention
      const mentionMatch = content.match(/@(\w+)/);
      if (mentionMatch) {
        const mentionedName = mentionMatch[1];
        const { agents } = get();
        const mentionedAgent = agents.find(a => 
          a.name.toLowerCase() === mentionedName.toLowerCase() ||
          a.custom_name?.toLowerCase() === mentionedName.toLowerCase()
        );
        if (mentionedAgent) {
          targetAgentId = mentionedAgent.id;
        }
      }
    }
    
    // Add agent to processing set
    if (targetAgentId) {
      const newProcessingAgents = new Set(processingAgents);
      newProcessingAgents.add(targetAgentId);
      set({ processingAgents: newProcessingAgents });
    } else {
      // If no specific agent, add a generic processing indicator
      const newProcessingAgents = new Set(processingAgents);
      newProcessingAgents.add('system');
      set({ processingAgents: newProcessingAgents });
    }
    
    try {
      // Parse mentions
      const mentions = [];
      if (content.includes('@all')) {
        mentions.push({ type: 'all' });
      }

      // Send typing stop
      if (socket) {
        socket.emit('typing:stop', { conversationId: currentConversation.id });
      }

      // Don't send agentId for user messages - let the backend handle agent selection
      const response = await conversationService.sendMessage(currentConversation.id, {
        content,
        agentId: agentId, // Only send if explicitly specified
        parentMessageId,
      });

      console.log('[ChatStore] Message sent, response:', response);
      
      // If WebSocket doesn't add the message, add it manually
      if (response && response.id) {
        const { messages } = get();
        const messageExists = messages.some(m => m.id === response.id);
        if (!messageExists) {
          console.log('[ChatStore] Adding message manually since WebSocket did not');
          set({ messages: [...messages, response] });
        }
      }
    } catch (error) {
      console.error('[ChatStore] Failed to send message:', error);
      throw error;
    } finally {
      set({ isSendingMessage: false } as any);
    }
  },

  editMessage: async (messageId: string, newContent: string) => {
    try {
      await conversationService.editMessage(messageId, newContent);
      // Message will be updated via WebSocket
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  },

  addReaction: async (messageId: string, emoji: string) => {
    try {
      await conversationService.addReaction(messageId, emoji);
      // Reaction will be added via WebSocket
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  },

  removeReaction: async (messageId: string, emoji: string) => {
    try {
      await conversationService.removeReaction(messageId, emoji);
      // Update local state
      const { messages } = get();
      set({
        messages: messages.map((msg) => {
          if (msg.id === messageId && msg.reactions) {
            return {
              ...msg,
              reactions: msg.reactions.filter((r) => r.emoji !== emoji),
            };
          }
          return msg;
        }),
      });
    } catch (error) {
      console.error('Failed to remove reaction:', error);
    }
  },

  setCurrentConversation: (conversation: Conversation | null) => {
    const { socket, currentConversation } = get();
    
    // Leave previous conversation
    if (socket && currentConversation) {
      socket.emit('leave:conversation', currentConversation.id);
    }
    
    set({ currentConversation: conversation, messages: [] });
    
    // Join new conversation
    if (socket && conversation) {
      socket.emit('join:conversation', conversation.id);
    }
  },

  toggleHtmlRenderer: () => {
    set({ showHtmlRenderer: !get().showHtmlRenderer });
  },

  updateAgentStatus: (agentId: string, status: Agent['status']) => {
    const { agents } = get();
    set({
      agents: agents.map((agent) =>
        agent.id === agentId ? { ...agent, status } : agent
      ),
    });
  },

  clearTaskSuggestions: () => {
    set({
      taskSuggestions: [],
      availableTeammates: [],
      taskSuggestionsMetadata: undefined,
      showTaskSuggestions: false
    });
  },

  detectTasksFromMessage: async (messageId: string) => {
    console.log('[ChatStore] detectTasksFromMessage called with messageId:', messageId);
    try {
      const message = get().messages.find(m => m.id === messageId);
      if (!message) {
        console.error('[ChatStore] Message not found:', messageId);
        console.error('[ChatStore] Available message IDs:', get().messages.map(m => m.id));
        return;
      }
      console.log('[ChatStore] Found message:', message);

      // Only detect tasks from assistant messages
      if (message.role !== 'assistant') {
        console.log('[ChatStore] Can only detect tasks from assistant messages, got role:', message.role);
        return;
      }

      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        console.error('[ChatStore] No auth token found');
        return;
      }
      console.log('[ChatStore] Auth token found, proceeding with task detection');

      // Set loading state
      set({ isDetectingTasks: true });
      console.log('[ChatStore] Loading state set to true');

      // Call the agent service to detect tasks
      const url = `${API_CONFIG.AGENT_SERVICE_URL}/api/v1/tools/detect-tasks`;
      console.log('[ChatStore] Calling task detection API at:', url);

      const requestBody = {
        messageId,
        content: message.content,
        conversationId: message.conversation_id,
        agentName: message.metadata?.agentName || 'Unknown'
      };
      console.log('[ChatStore] Request body:', requestBody);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          messageId,
          content: message.content,
          conversationId: message.conversation_id,
          agentName: message.metadata?.agentName || 'Unknown'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatStore] API error response:', response.status, errorText);
        throw new Error(`Failed to detect tasks: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('[ChatStore] Task detection API response:', result);

      // New async behavior - we get a jobId and need to poll for results
      if (result.status === 'processing' && result.jobId) {
        console.log('[ChatStore] Task detection is processing in background, jobId:', result.jobId);
        console.log('[ChatStore] Estimated time:', result.estimatedTime);

        // Log message for user (UI should show this via isDetectingTasks state)
        console.log('[ChatStore]', result.message || 'Task detection is running in the background. This may take a couple of minutes to complete.');

        // Poll for job status every 3 seconds
        const pollInterval = setInterval(async () => {
          try {
            const statusUrl = `${API_CONFIG.AGENT_SERVICE_URL}/api/v1/tools/detect-tasks/status/${result.jobId}`;
            const statusResponse = await fetch(statusUrl, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (!statusResponse.ok) {
              console.error('[ChatStore] Status check failed:', statusResponse.status);
              clearInterval(pollInterval);
              set({ isDetectingTasks: false });
              console.error('[ChatStore] Failed to check task detection status');
              return;
            }

            const statusResult = await statusResponse.json();
            console.log('[ChatStore] Job status:', statusResult.status, 'Tasks:', statusResult.tasks?.length);

            if (statusResult.status === 'completed') {
              clearInterval(pollInterval);

              if (statusResult.tasks && statusResult.tasks.length > 0) {
                console.log('[ChatStore] Tasks detected:', statusResult.tasks.length);
                // Fetch available teammates
                const agents = await agentService.getAgents();
                const formattedAgents = agents.map((agent: any) => ({
                  id: agent.id,
                  name: agent.custom_name || agent.name,
                  type: agent.type,
                  isCustom: agent.is_custom || false
                }));

                console.log('[ChatStore] Setting state with tasks and showing dialog');
                set({
                  taskSuggestions: statusResult.tasks,
                  availableTeammates: formattedAgents,
                  taskSuggestionsMetadata: {
                    sourceAgent: message.metadata?.agentName || 'Unknown',
                    detectedAt: new Date().toISOString(),
                    conversationId: message.conversation_id
                  },
                  showTaskSuggestions: true,
                  isDetectingTasks: false
                });
                console.log('[ChatStore] State updated - showTaskSuggestions should be true now');
              } else {
                console.log('[ChatStore] No tasks detected in message');
                set({
                  taskSuggestions: [],
                  availableTeammates: [],
                  taskSuggestionsMetadata: {
                    sourceAgent: message.metadata?.agentName || 'Unknown',
                    detectedAt: new Date().toISOString()
                  },
                  showTaskSuggestions: false,
                  isDetectingTasks: false
                });
                console.log('[ChatStore] No tasks were detected in this message.');
              }
            } else if (statusResult.status === 'failed') {
              clearInterval(pollInterval);
              set({ isDetectingTasks: false });
              console.error('[ChatStore] Task detection failed:', statusResult.error || 'Unknown error');
            }
            // If status is still 'processing', continue polling
          } catch (pollError) {
            console.error('[ChatStore] Error polling job status:', pollError);
            clearInterval(pollInterval);
            set({ isDetectingTasks: false });
            console.error('[ChatStore] Error checking task detection status');
          }
        }, 3000); // Poll every 3 seconds

        // Set a maximum timeout of 3 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          const currentState = get();
          if (currentState.isDetectingTasks) {
            set({ isDetectingTasks: false });
            console.warn('[ChatStore] Task detection timed out after 3 minutes.');
          }
        }, 180000); // 3 minutes max
      } else if (result.tasks && result.tasks.length > 0) {
        // Fallback for old synchronous response (backward compatibility)
        console.log('[ChatStore] Tasks detected (synchronous):', result.tasks.length);
        const agents = await agentService.getAgents();
        const formattedAgents = agents.map((agent: any) => ({
          id: agent.id,
          name: agent.custom_name || agent.name,
          type: agent.type,
          isCustom: agent.is_custom || false
        }));

        set({
          taskSuggestions: result.tasks,
          availableTeammates: formattedAgents,
          taskSuggestionsMetadata: {
            sourceAgent: message.metadata?.agentName || 'Unknown',
            detectedAt: new Date().toISOString(),
            conversationId: message.conversation_id
          },
          showTaskSuggestions: true,
          isDetectingTasks: false
        });
      } else {
        console.log('[ChatStore] No tasks detected');
        set({ isDetectingTasks: false });
        console.log('[ChatStore] No tasks were detected in this message.');
      }
    } catch (error) {
      console.error('[ChatStore] Error detecting tasks:', error);
      console.error('[ChatStore] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      set({ isDetectingTasks: false });

      // Show more specific error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[ChatStore] Error detecting tasks:', errorMessage);
    }
  },

  // --- Messaging actions ---

  loadChannels: async () => {
    try {
      const { messagingService } = await import('../services/api/messaging.service');
      const channels = await messagingService.getChannels();
      set({ channels: channels as any[] });
    } catch (error) {
      console.error('[ChatStore] Failed to load channels:', error);
    }
  },

  markConversationRead: (conversationId: string) => {
    const { socket, unreadCounts } = get();
    // Emit via WebSocket
    if (socket) {
      socket.emit('mark:read', { conversationId });
    }
    // Update local state
    const newCounts = new Map(unreadCounts);
    newCounts.delete(conversationId);
    set({ unreadCounts: newCounts });
  },

  updateUnreadCount: (conversationId: string, count: number, hasMention: boolean) => {
    const { unreadCounts } = get();
    const newCounts = new Map(unreadCounts);
    if (count === 0) {
      newCounts.delete(conversationId);
    } else {
      newCounts.set(conversationId, { conversationId, count, hasMention });
    }
    set({ unreadCounts: newCounts });
  },

  emitTyping: (isTyping: boolean) => {
    const { socket, currentConversation } = get();
    if (socket && currentConversation) {
      socket.emit(isTyping ? 'typing:start' : 'typing:stop', {
        conversationId: currentConversation.id,
      });
    }
  },
}));